/**
 * Provider org status helpers.
 *
 * List endpoints expose `is_active` (aliased from `active_on_platform`).
 * Detail GET Providers/{id} exposes `active_on_platform` (and may omit `is_active`).
 * `discovery_status` is the same boolean field in both (Visible / Hidden).
 */

/** Normalize detail payloads so consumers can rely on `is_active`. */
export function normalizeProviderOrgDetails(data) {
  if (!data) return data;
  if (data.is_active == null && data.active_on_platform != null) {
    return { ...data, is_active: data.active_on_platform };
  }
  return data;
}

/** Resolve org Active/Inactive from either field name. */
export function resolveOrgIsActive(org) {
  if (!org) return undefined;
  const value = org.is_active ?? org.active_on_platform;
  return typeof value === "boolean" ? value : undefined;
}

/** Resolve org Visible/Hidden from discovery_status. */
export function resolveOrgDiscoveryStatus(org) {
  if (!org) return undefined;
  return typeof org.discovery_status === "boolean"
    ? org.discovery_status
    : undefined;
}

/**
 * Merge status fields from an API org payload into previous UI state.
 * Uses nullish coalescing so a real `false` is preserved.
 */
export function mergeOrgStatusFromApi(incoming, prev = {}) {
  if (!incoming) {
    return {
      is_active: prev.is_active,
      discovery_status: prev.discovery_status,
    };
  }
  return {
    is_active: resolveOrgIsActive(incoming) ?? prev.is_active,
    discovery_status:
      resolveOrgDiscoveryStatus(incoming) ?? prev.discovery_status,
  };
}

/** Badges should not render placeholder Inactive/Hidden while status is unknown. */
export function shouldShowOrgStatusBadge(value) {
  return typeof value === "boolean";
}

/**
 * Visible/Hidden (discovery) is vendor-org only — including admin viewing a vendor.
 * Sponsor manage-account (and admin viewing a sponsor) must never show this badge.
 */
export function shouldShowDiscoveryStatusBadge(discoveryStatus, isVendorOrg) {
  return Boolean(isVendorOrg) && shouldShowOrgStatusBadge(discoveryStatus);
}

export function getActiveStatusLabel(isActive) {
  if (!shouldShowOrgStatusBadge(isActive)) return null;
  return isActive ? "Active" : "Inactive";
}

export function getDiscoveryStatusLabel(discoveryStatus) {
  if (!shouldShowOrgStatusBadge(discoveryStatus)) return null;
  return discoveryStatus ? "Visible" : "Hidden";
}
